import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Alert, Button, Empty, Input, Link, Message, Modal, Radio, Select,
  Space, Spin, Tag, Typography,
} from "@arco-design/web-react";
import { IconCopy, IconPlus, IconDownload, IconLeft, IconUp, IconDown } from "@arco-design/web-react/icon";
import moment from "moment";

import Content from "../Layout/Content";
import SummaryCard from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { DeleteApi, FetchApi, PostApi } from "../Api/AuthenticatedApi";
import { flowOptions, formatDelay, formatOffset, heldBack, personaLine, plantedTooRecently, reasonSummary, watchReadiness } from "./spyWatch";
import {
  blockRemote, filterMessages, inboxFilters, isUnread, labelMessages, loadShowImages, rowTag, saveShowImages, setViewed, step,
} from "./spyInbox";
import { emailParts } from "./emailParts";

const { Text, Paragraph } = Typography;

const PANE_HEIGHT = "max(420px, calc(100vh - 320px))";

// useNarrow: phone width, where the list and the email take turns on screen.
function useNarrow() {
  const query = "(max-width: 767px)";
  const get = () => {
    try { return window.matchMedia(query).matches; } catch (e) { return false; }
  };
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    let media;
    try { media = window.matchMedia(query); } catch (e) { return undefined; }
    const onChange = () => setNarrow(media.matches);
    if (media.addEventListener) media.addEventListener("change", onChange);
    return () => { if (media.removeEventListener) media.removeEventListener("change", onChange); };
  }, []);
  return narrow;
}

export default function WatchDetail() {
  const { watchId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const narrow = useNarrow();

  const [watch, setWatch] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [messages, setMessages] = useState([]);
  const [flows, setFlows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [addressFilter, setAddressFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [sources, setSources] = useState({});
  const [showImages, setShowImages] = useState(() => loadShowImages(watchId));
  const [importing, setImporting] = useState(false);
  const [importName, setImportName] = useState("");
  const [importGroup, setImportGroup] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const authFail = useCallback(() => navigate("/login"), [navigate]);

  const load = useCallback(() => {
    setLoading(true);
    FetchApi(getAccessTokenSilently, `/api/spy/watches/${watchId}`,
      (data) => { setWatch(data); setLoading(false); }, authFail,
      () => { setLoading(false); Message.error("Failed to load this watch"); });
    FetchApi(getAccessTokenSilently, `/api/spy/watches/${watchId}/addresses`,
      (data) => setAddresses(Array.isArray(data) ? data : []), authFail, () => {});
    FetchApi(getAccessTokenSilently, `/api/spy/watches/${watchId}/messages`,
      (data) => setMessages(Array.isArray(data) ? data : []), authFail, () => {});
    FetchApi(getAccessTokenSilently, `/api/spy/watches/${watchId}/flows`,
      (data) => setFlows(data || null), authFail, () => {});
  }, [getAccessTokenSilently, watchId, authFail]);

  useEffect(load, [load]);

  const labels = useMemo(() => labelMessages(flows), [flows]);
  const chips = useMemo(() => inboxFilters(flows, messages, labels), [flows, messages, labels]);
  const list = useMemo(
    () => filterMessages(messages, labels, filter, addressFilter),
    [messages, labels, filter, addressFilter],
  );
  const addressById = useMemo(() => new Map(addresses.map((a) => [a.id, a])), [addresses]);
  const current = list.find((m) => m.id === selectedId) || null;
  const position = current ? list.indexOf(current) : -1;

  // A filter that no longer exists (its sequence was re-sorted) falls back to All.
  useEffect(() => {
    if (!chips.some((c) => c.key === filter)) setFilter("all");
  }, [chips, filter]);

  // On a wide screen something is always open: the first message, or the
  // first one left after a filter hid the open one.
  useEffect(() => {
    if (!narrow && !current && list.length) setSelectedId(list[0].id);
  }, [narrow, current, list]);

  // Load the open email once; going back to it uses the copy already here.
  const currentId = current && current.id;
  useEffect(() => {
    if (!currentId || sources[currentId]) return;
    setSources((s) => ({ ...s, [currentId]: { loading: true } }));
    FetchApi(getAccessTokenSilently, `/api/spy/messages/${currentId}/source`,
      (data) => setSources((s) => ({ ...s, [currentId]: { ...data, loading: false } })), authFail,
      () => setSources((s) => ({ ...s, [currentId]: { loading: false, failed: true } })));
  }, [currentId, sources, getAccessTokenSilently, authFail]);

  // Opening an email marks it read, once per opening. "Mark unread" keeps it
  // unread until another email is opened.
  const markedId = useRef(null);
  useEffect(() => {
    if (!current || markedId.current === current.id) return;
    markedId.current = current.id;
    if (!isUnread(current)) return;
    const id = current.id;
    setMessages((ms) => setViewed(ms, id, new Date().toISOString()));
    PostApi(getAccessTokenSilently, `/api/spy/messages/${id}/viewed`, () => {}, authFail,
      () => setMessages((ms) => setViewed(ms, id, null)), {});
  }, [current, getAccessTokenSilently, authFail]);

  const markUnread = (id) => {
    const before = messages.find((m) => m.id === id);
    setMessages((ms) => setViewed(ms, id, null));
    DeleteApi(getAccessTokenSilently, `/api/spy/messages/${id}/viewed`, () => {}, authFail, () => {
      setMessages((ms) => setViewed(ms, id, before ? before.viewed_date : null));
      Message.error("Couldn't mark it unread");
    });
  };

  const move = useCallback((by) => {
    const next = step(list, selectedId, by);
    if (next) setSelectedId(next);
  }, [list, selectedId]);

  // Up/down arrows (and k/j) go through the list, unless typing somewhere.
  useEffect(() => {
    const onKey = (e) => {
      if (importOpen || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))) return;
      if (e.key === "ArrowDown" || e.key === "j") { e.preventDefault(); move(1); }
      if (e.key === "ArrowUp" || e.key === "k") { e.preventDefault(); move(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, importOpen]);

  // Keep the open row in view while stepping through.
  useEffect(() => {
    const row = currentId && document.getElementById(`spy-msg-${currentId}`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: "nearest" });
  }, [currentId]);

  const toggleImages = () => {
    saveShowImages(watchId, !showImages);
    setShowImages(!showImages);
  };

  const addAddress = () => {
    PostApi(getAccessTokenSilently, `/api/spy/watches/${watchId}/addresses`,
      () => { Message.success("New address ready to paste"); load(); },
      authFail,
      (err) => Message.error((err && err.message) || "Failed to add an address"),
      {});
  };

  const copy = (value) => {
    // navigator.clipboard needs a secure context; the textarea fallback keeps
    // the primary action of this whole screen working over plain http in dev.
    const done = () => Message.success("Address copied — paste it into their signup form");
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(done, () => Message.error("Couldn't copy"));
      return;
    }
    const field = document.createElement("textarea");
    field.value = value;
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    try { document.execCommand("copy"); done(); } catch (e) { Message.error("Couldn't copy"); }
    field.remove();
  };

  const openImport = (group) => {
    const options = flowOptions(flows);
    setImportGroup(group || (options.length === 1 ? options[0].value : ""));
    setImportName("");
    setImportOpen(true);
  };

  const runImport = () => {
    setImporting(true);
    PostApi(getAccessTokenSilently, `/api/spy/watches/${watchId}/import`,
      (data) => {
        setImporting(false);
        setImportOpen(false);
        Message.success("Draft sequence created");
        if (data && data.sequence && data.sequence.id) navigate(`/sequences/${data.sequence.id}/edit`);
      },
      authFail,
      (err) => { setImporting(false); Message.error((err && err.message) || "Failed to import"); },
      { group: importGroup || undefined, name: importName.trim() || undefined });
  };

  if (loading && !watch) {
    return <Content><Spin /></Content>;
  }

  const readiness = watchReadiness(watch);
  const tooClose = plantedTooRecently(addresses.filter((a) => !heldBack(a)));
  const waiting = addresses.some((a) => heldBack(a));
  const options = flowOptions(flows);
  const activeChip = chips.find((c) => c.key === filter);
  // On a phone the email replaces the list once one is tapped.
  const showList = !narrow || !current;
  const showReader = !narrow || !!current;

  return (
    <Content>
      <SimpleBreadcrumbs
        elements={[{ name: "Email Spy", path: "/spy" }, { name: (watch && watch.name) || "Watch" }]}
      />

      {/* The list remembers its own sort, so plain /spy comes back sorted. */}
      <Button size="small" icon={<IconLeft />} style={{ margin: "8px 0" }} onClick={() => navigate("/spy")}>
        All competitors
      </Button>

      <SummaryCard
        title={(watch && watch.name) || "Watch"}
        subTitle={watch && watch.signup_url ? watch.signup_url : "No signup page saved"}
        titleElement={<Tag color={readiness.ready ? "green" : "orange"}>{readiness.status}</Tag>}
      >
        {!readiness.ready && (
          <Alert
            type="info"
            style={{ marginBottom: 12 }}
            title={{
              "needs a second address": "Plant a second address",
              "second address coming": "Your second address is on its way",
            }[readiness.status] || "Nothing has arrived yet"}
            content={
              <span>
                {readiness.hint}
                {watch && watch.signup_url && (
                  <> <Link href={watch.signup_url} target="_blank" rel="noreferrer">Open their signup page</Link></>
                )}
              </span>
            }
          />
        )}

        {tooClose && (
          <Alert
            type="warning"
            style={{ marginBottom: 12 }}
            title="These addresses signed up too close together"
            content="Both addresses joined within half a day of each other, so a drip and a blast still look identical. Leave a few days between signups."
          />
        )}

        <AddressStrip
          addresses={addresses}
          waiting={waiting}
          signupUrl={watch && watch.signup_url}
          onCopy={copy}
          onAdd={addAddress}
        />

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, margin: "12px 0" }}>
          <Space wrap size={4}>
            {chips.map((chip) => (
              <Tag
                key={chip.key}
                checkable
                checked={filter === chip.key}
                onCheck={() => setFilter(chip.key)}
              >
                {chip.label} {chip.count}
              </Tag>
            ))}
          </Space>
          {addresses.length > 1 && (
            <Select
              size="small"
              style={{ width: 240, marginLeft: "auto" }}
              value={addressFilter}
              onChange={setAddressFilter}
              options={[
                { label: "Sent to any address", value: "" },
                ...addresses.filter((a) => !heldBack(a)).map((a) => ({ label: `To ${a.address}`, value: a.id })),
              ]}
            />
          )}
        </div>

        {activeChip && activeChip.group && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Text type="secondary" style={{ flex: 1 }}>
              What they send everyone who signs up, in order, with the wait before each one.
            </Text>
            <Button type="primary" size="small" icon={<IconDownload />} onClick={() => openImport(activeChip.group)}>
              Copy as draft sequence
            </Button>
          </div>
        )}
        {filter === "unsorted" && <UnsortedNote flows={flows} />}

        <div style={{ display: "flex", gap: 12, alignItems: "stretch" }}>
          {showList && (
            <div style={{
              flex: narrow ? "1 1 auto" : "0 0 340px", minWidth: 0, height: PANE_HEIGHT, overflowY: "auto",
              border: "1px solid var(--color-border-2)", borderRadius: 4,
            }}>
              {list.length === 0 ? (
                <Empty style={{ marginTop: 40 }} description={messages.length ? "Nothing matches this filter" : "Nothing collected yet"} />
              ) : list.map((m) => (
                <InboxRow
                  key={m.id}
                  message={m}
                  label={labels.get(m.id)}
                  inSequence={!!(activeChip && activeChip.group)}
                  selected={m.id === selectedId}
                  onSelect={() => setSelectedId(m.id)}
                />
              ))}
            </div>
          )}
          {showReader && (
            <div style={{
              flex: "1 1 auto", minWidth: 0, height: PANE_HEIGHT, display: "flex", flexDirection: "column",
              border: "1px solid var(--color-border-2)", borderRadius: 4, padding: 12,
            }}>
              {current ? (
                <Reader
                  message={current}
                  label={labels.get(current.id)}
                  address={addressById.get(current.address_id)}
                  source={sources[current.id]}
                  showImages={showImages}
                  onToggleImages={toggleImages}
                  position={position}
                  total={list.length}
                  onMove={move}
                  onBack={narrow ? () => setSelectedId(null) : null}
                  onMarkUnread={() => markUnread(current.id)}
                />
              ) : (
                <Empty style={{ marginTop: 40 }} description="Pick an email to read it" />
              )}
            </div>
          )}
        </div>
      </SummaryCard>

      <Modal
        visible={importOpen}
        title="Copy as a draft sequence"
        okText="Create draft"
        confirmLoading={importing}
        onOk={runImport}
        onCancel={() => setImportOpen(false)}
      >
        <Paragraph style={{ color: "var(--color-text-3)" }}>
          We'll create a sequence with the same number of emails and the same waits
          between them, and their subject lines to rewrite. Their artwork isn't copied.
          Every step arrives switched off.
        </Paragraph>
        {options.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Which sequence</Text>
            <Space wrap>
              {options.map((option) => (
                <Tag
                  key={option.value}
                  checkable
                  checked={importGroup === option.value}
                  onCheck={() => setImportGroup(option.value)}
                >
                  {option.label}
                </Tag>
              ))}
            </Space>
          </div>
        )}
        <Text type="secondary">Name it (optional)</Text>
        <Input value={importName} onChange={setImportName} placeholder="My take on their welcome series" />
      </Modal>
    </Content>
  );
}

// AddressStrip: the seed addresses, to copy into their signup form.
function AddressStrip({ addresses, waiting, signupUrl, onCopy, onAdd }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
      <Text type="secondary">Addresses — paste one into their signup form:</Text>
      {addresses.length === 0 && <Text type="secondary">none yet</Text>}
      {addresses.map((a) => heldBack(a) ? (
        // Minted with the first, shown once the wait is over.
        <Tag key={a.id}>Next one ready {moment(a.available_date).format("MMM D [at] h:mm a")}</Tag>
      ) : (
        <div key={a.id}>
          <Space size={2}>
            <Text code>{a.address}</Text>
            <Button size="mini" type="text" icon={<IconCopy />} aria-label={`Copy ${a.address}`} onClick={() => onCopy(a.address)} />
          </Space>
          {personaLine(a) && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>{personaLine(a)}</Text></div>
          )}
        </div>
      ))}
      {!waiting && (
        <Button size="mini" type="outline" icon={<IconPlus />} onClick={onAdd}>Add address</Button>
      )}
      {signupUrl && <Link href={signupUrl} target="_blank" rel="noreferrer">Signup page</Link>}
    </div>
  );
}

function UnsortedNote({ flows }) {
  const reasons = reasonSummary(flows && flows.unclassified);
  if (!reasons.length) return null;
  return (
    <Alert
      type="info"
      style={{ marginBottom: 12 }}
      title="Why these aren't sorted yet"
      content={
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {reasons.map(({ reason, count }) => (
            <li key={reason}>{count} — {reason}</li>
          ))}
        </ul>
      }
    />
  );
}

function InboxRow({ message, label, inSequence, selected, onSelect }) {
  const tag = rowTag(label);
  const unread = isUnread(message);
  const sent = moment(message.sent_date || message.received_date);
  // In a sequence, where it falls matters more than the date.
  const when = inSequence && label && label.kind === "step"
    ? formatOffset(label.offsetMinutes)
    : sent.format(sent.isSame(moment(), "year") ? "MMM D" : "MMM D, YYYY");
  return (
    <div
      id={`spy-msg-${message.id}`}
      role="button"
      tabIndex={-1}
      onClick={onSelect}
      style={{
        padding: "8px 12px", cursor: "pointer",
        borderBottom: "1px solid var(--color-border-1)",
        background: selected ? "var(--color-primary-light-1)" : "transparent",
        borderLeft: `3px solid ${selected ? "rgb(var(--primary-6))" : "transparent"}`,
      }}
    >
      {/* Like an inbox: unread is bold with a dot. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {unread && (
          <span
            aria-label="Unread"
            style={{ flex: "0 0 8px", height: 8, borderRadius: "50%", background: "rgb(var(--primary-6))" }}
          />
        )}
        <span style={{
          fontWeight: unread ? 700 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {message.subject || "(no subject)"}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-3)" }}>
        <Tag size="small" color={tag.color}>{tag.text}</Tag>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {message.from_name || message.from_domain}
        </span>
        <span>{when}</span>
      </div>
    </div>
  );
}

// context says where an email falls: which step, a blast, or not sorted.
function context(label) {
  if (label && label.kind === "step") {
    let text = `${label.flowLabel}, step ${label.step} of ${label.steps} · ${formatOffset(label.offsetMinutes)}`;
    if (label.step > 1) text += ` · ${formatDelay(label.delayMinutes)} after the one before`;
    return text;
  }
  if (label && label.kind === "blast") {
    return `One-off blast to their whole list${label.sentOn ? ` on ${label.sentOn}` : ""}`;
  }
  return `Not sorted yet${label && label.reason ? `: ${label.reason}` : ""}`;
}

function Reader({ message, label, address, source, showImages, onToggleImages, position, total, onMove, onBack, onMarkUnread }) {
  const sent = moment(message.sent_date || message.received_date);
  // Stays on the same view while stepping through emails.
  const [view, setView] = useState("email");
  const ready = source && !source.loading && !source.failed;
  const parts = useMemo(() => (ready ? emailParts(source.html, source.text) : null), [ready, source]);
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        {onBack && <Button size="small" icon={<IconLeft />} onClick={onBack}>List</Button>}
        <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>{position + 1} of {total} · ↑ ↓ to move</Text>
        {!isUnread(message) && <Button size="small" onClick={onMarkUnread}>Mark unread</Button>}
        <Button size="small" icon={<IconUp />} aria-label="Previous email" disabled={position <= 0} onClick={() => onMove(-1)} />
        <Button size="small" icon={<IconDown />} aria-label="Next email" disabled={position >= total - 1} onClick={() => onMove(1)} />
      </div>

      <div style={{ fontSize: 16, fontWeight: 600 }}>{message.subject || "(no subject)"}</div>
      <div style={{ fontSize: 12, color: "var(--color-text-2)", marginTop: 2 }}>
        {message.from_name} &lt;{message.from_address}&gt;
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-3)" }}>
        {address ? `To ${address.address} · ` : ""}{sent.format("MMM D, YYYY h:mm A")}
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-3)" }}>{context(label)}</div>
      {parts && (parts.tool || parts.trackers > 0) && (
        <div style={{ fontSize: 12, color: "var(--color-text-3)" }}>
          {parts.tool && <>Sent with {parts.tool}</>}
          {parts.tool && parts.trackers > 0 && " · "}
          {parts.trackers > 0 && (
            <Text style={{ fontSize: 12, color: "rgb(var(--orange-6))" }}>
              {parts.trackers === 1 ? "1 open tracker" : `${parts.trackers} open trackers`}
            </Text>
          )}
        </div>
      )}

      {parts && (
        <Radio.Group type="button" size="small" value={view} onChange={setView} style={{ margin: "8px 0" }}>
          <Radio value="email">Email</Radio>
          <Radio value="links">Links {parts.links.length}</Radio>
          <Radio value="images">Images {parts.images.length}</Radio>
        </Radio.Group>
      )}

      {(!source || source.loading) && <Spin style={{ marginTop: 24 }} />}
      {source && source.failed && <Alert type="error" content="Couldn't load this email." />}
      {parts && view === "links" && <LinkList links={parts.links} />}
      {parts && view === "images" && <ImageList images={parts.images} showImages={showImages} />}
      {ready && view === "email" && (
        source.html ? (
          <>
            <div style={{
              display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", marginBottom: 8,
              fontSize: 12, borderRadius: 4, background: "var(--color-fill-2)",
            }}>
              <span style={{ flex: 1 }}>
                {showImages
                  ? "Images are on. Opening an email loads its images, which tells them it was opened."
                  : "Images are off, so they can't tell you opened this."}
              </span>
              <Button size="mini" type={showImages ? "secondary" : "primary"} onClick={onToggleImages}>
                {showImages ? "Turn images off" : "Show images"}
              </Button>
            </div>
            {/* Sandboxed with no allowances at all: this is a competitor's
                markup, so no scripts, no forms, and no access to our origin. */}
            <iframe
              title="Original email"
              sandbox=""
              srcDoc={showImages ? source.html : blockRemote(source.html)}
              style={{ flex: 1, width: "100%", border: "1px solid var(--color-border-2)", background: "#fff" }}
            />
          </>
        ) : (
          <pre style={{ flex: 1, overflow: "auto", whiteSpace: "pre-wrap", color: "var(--color-text-2)", margin: 0 }}>
            {source.text}
          </pre>
        )
      )}
    </>
  );
}

const partRow = { padding: "6px 0", borderBottom: "1px solid var(--color-border-1)" };
const partUrl = { fontSize: 12, color: "var(--color-text-3)", wordBreak: "break-all" };

// LinkList shows each link as text to read or copy. Nothing here opens it:
// a click on a tracked link tells the sender, and on an unsubscribe link
// it would take the seed off their list.
function LinkList({ links }) {
  if (!links.length) return <Empty style={{ marginTop: 24 }} description="No links in this email" />;
  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      {links.map((link) => (
        <div key={link.url} style={partRow}>
          <Space size={4} wrap>
            <Text style={{ fontWeight: 600 }}>{link.text || "(no text)"}</Text>
            {link.count > 1 && <Text type="secondary" style={{ fontSize: 12 }}>×{link.count}</Text>}
            {link.kind === "unsubscribe" && <Tag size="small" color="red">Unsubscribe</Tag>}
            {link.kind === "confirm" && <Tag size="small" color="green">Confirm</Tag>}
            {link.tracked && <Tag size="small" color="orange">Click tracked{link.tool ? ` · ${link.tool}` : ""}</Tag>}
          </Space>
          {link.goesTo && <div style={{ fontSize: 12 }}>Goes to {link.goesTo}</div>}
          <div style={partUrl}>{link.url}</div>
        </div>
      ))}
    </div>
  );
}

// ImageList shows the images, trackers first. Pictures only show while
// images are on, since showing one here loads it just like the email would.
function ImageList({ images, showImages }) {
  if (!images.length) return <Empty style={{ marginTop: 24 }} description="No images in this email" />;
  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      {images.map((image) => (
        <div key={image.url} style={{ ...partRow, display: "flex", gap: 10 }}>
          <div style={{
            flex: "0 0 56px", height: 56, display: "flex", alignItems: "center", justifyContent: "center",
            background: "var(--color-fill-2)", borderRadius: 4, overflow: "hidden", fontSize: 11, color: "var(--color-text-3)",
          }}>
            {showImages
              ? <img src={image.url} alt={image.alt || ""} referrerPolicy="no-referrer" style={{ maxWidth: 56, maxHeight: 56 }} />
              : "off"}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Space size={4} wrap>
              {image.tracker && <Tag size="small" color="orange">Open tracker{image.tool ? ` · ${image.tool}` : ""}</Tag>}
              <Text style={{ fontSize: 12 }}>
                {[
                  image.alt && `“${image.alt}”`,
                  image.width != null && image.height != null && `${image.width}×${image.height}`,
                  image.background && "background",
                  image.hidden && "hidden",
                ].filter(Boolean).join(" · ") || image.host}
              </Text>
            </Space>
            {image.tracker && <div style={{ fontSize: 12 }}>Looks like a tracker: {image.tracker}.</div>}
            <div style={partUrl}>{image.url}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
